import { Component, HostListener, computed, inject } from "@angular/core";
import { CommonModule } from "@angular/common";
import { TourService } from "./tour.service";

/** Full-screen spotlight overlay for the guided tour. A highlight box hugs the
 *  target element (dimming everything else via a huge box-shadow) and a card
 *  with the step copy is positioned next to it — or centered for steps with
 *  no target. */
@Component({
  selector: "app-tour-overlay",
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="tour-layer" *ngIf="tour.active()">
      <div class="tour-dim" *ngIf="!tour.targetRect()"></div>
      <div class="tour-spot" *ngIf="tour.targetRect() as r" [style.top.px]="r.top - 6" [style.left.px]="r.left - 6" [style.width.px]="r.width + 12" [style.height.px]="r.height + 12"></div>

      <div class="tour-card" [ngStyle]="cardStyle()">
        <div class="tour-step-count">Step {{ tour.index() + 1 }} of {{ tour.steps().length }}</div>
        <h3>{{ tour.current()?.title }}</h3>
        <p>{{ tour.current()?.body }}</p>
        <div class="tour-actions">
          <button class="btn ghost" type="button" (click)="tour.finish()">Skip tour</button>
          <span style="flex:1"></span>
          <button class="btn ghost" type="button" *ngIf="tour.index() > 0" (click)="tour.prev()">Back</button>
          <button class="btn primary" type="button" (click)="tour.next()">
            {{ tour.index() >= tour.steps().length - 1 ? "Finish" : "Next" }}
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      .tour-layer { position: fixed; inset: 0; z-index: 3000; }
      .tour-dim { position: absolute; inset: 0; background: rgba(15, 23, 42, 0.55); }
      .tour-spot {
        position: absolute;
        border-radius: 12px;
        box-shadow: 0 0 0 9999px rgba(15, 23, 42, 0.55), 0 0 0 2px var(--accent, #2563eb);
        transition: top 0.25s ease, left 0.25s ease, width 0.25s ease, height 0.25s ease;
        pointer-events: none;
      }
      .tour-card {
        position: absolute;
        width: min(380px, calc(100vw - 32px));
        background: #fff;
        border-radius: 14px;
        padding: 18px 20px;
        box-shadow: 0 18px 50px rgba(15, 23, 42, 0.35);
      }
      .tour-step-count { font-size: 11.5px; font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase; color: var(--text-3, #64748b); margin-bottom: 6px; }
      .tour-card h3 { margin: 0 0 8px; font-size: 17px; }
      .tour-card p { margin: 0 0 14px; font-size: 13.5px; line-height: 1.55; color: var(--text-2, #334155); }
      .tour-actions { display: flex; align-items: center; gap: 8px; }
      .tour-actions .btn { border-radius: 8px; padding: 7px 14px; font-size: 13px; font-weight: 600; cursor: pointer; border: 1px solid transparent; }
      .tour-actions .btn.primary { background: var(--accent, #2563eb); color: #fff; }
      .tour-actions .btn.ghost { background: transparent; color: var(--text-2, #334155); border-color: var(--border, #e2e8f0); }
    `,
  ],
})
export class TourOverlayComponent {
  tour = inject(TourService);

  @HostListener("window:resize")
  onResize() {
    this.tour.remeasure();
  }

  /** Place the card beside the spotlighted element (right of the sidebar item
   *  on desktop, below it on mobile), clamped to the viewport; center it when
   *  there is no target. */
  cardStyle = computed(() => {
    const r = this.tour.targetRect();
    const cardW = Math.min(380, window.innerWidth - 32);
    if (!r) {
      return {
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
      };
    }
    const margin = 14;
    let left: number;
    let top: number;
    if (r.right + cardW + margin < window.innerWidth) {
      left = r.right + margin; // beside (desktop sidebar)
      top = Math.min(Math.max(r.top - 8, 16), window.innerHeight - 260);
    } else {
      left = Math.min(Math.max(r.left, 16), window.innerWidth - cardW - 16);
      top = r.bottom + margin; // below (mobile)
      if (top > window.innerHeight - 240) top = Math.max(r.top - 240, 16);
    }
    return { top: top + "px", left: left + "px" };
  });
}
